import React, { useMemo, useState } from 'react';
import { FaBus, FaTruck, FaGraduationCap, FaCheck, FaArrowLeft, FaArrowRight, FaPlus, FaBuilding } from 'react-icons/fa';
import './CreateOrgWizard.css';

export type OrgKind = 'FULL_FEATURE' | 'BUS_TRACKING' | 'LORRY';
export type LorryModeKind = 'SELF_TRACKING' | 'CARGO';

export interface CreateOrgPayload {
  kind: OrgKind;
  lorryMode: LorryModeKind;
  name: string;
  code: string;
  address: string;
  phone: string;
  email: string;
  plan: 'premium' | 'enterprise';
  expiry: string; // yyyy-mm-dd
  active: boolean;
}

interface CreateOrgWizardProps {
  onClose: () => void;
  /** Wire to real data later. Should return whether the org was created and the final code. */
  onCreate: (payload: CreateOrgPayload) => Promise<{ ok: boolean; fullCode?: string; error?: string }>;
  /** Pre-select the org kind when opening from a context-specific entry point. */
  initialKind?: OrgKind;
  initialLorryMode?: LorryModeKind;
}

const ORG_KINDS: {
  id: OrgKind;
  title: string;
  icon: React.ReactNode;
  grad: string;
  desc: string;
  suffix: string;
  roles: string;
}[] = [
  {
    id: 'FULL_FEATURE',
    title: 'Full Feature',
    icon: <FaGraduationCap />,
    grad: 'linear-gradient(135deg,#9b7cff,#6f4ff0)',
    desc: 'Schools — students, teachers, parents, diary, menus and more.',
    suffix: '',
    roles: 'Admin · Teacher · Parent · Student',
  },
  {
    id: 'BUS_TRACKING',
    title: 'Bus tracking only',
    icon: <FaBus />,
    grad: 'linear-gradient(135deg,#22c3ee,#2f7cf0)',
    desc: 'School bus routes — fleet admin, bus driver, parent.',
    suffix: '.bus',
    roles: 'Fleet Admin · Bus Driver · Parent',
  },
  {
    id: 'LORRY',
    title: 'Lorry',
    icon: <FaTruck />,
    grad: 'linear-gradient(135deg,#ffb27a,#f5872f)',
    desc: 'Commercial fleet — personal tracking or cargo with clients.',
    suffix: '',
    roles: '',
  },
];

const LORRY_MODES: Record<LorryModeKind, { label: string; suffix: string; roles: string; note: string }> = {
  SELF_TRACKING: { label: 'Personal tracking', suffix: '.fleet', roles: 'Owner · Driver', note: 'Owner + driver self-tracking.' },
  CARGO: { label: 'Cargo', suffix: '.cargo', roles: 'Owner · Driver · Client', note: 'Live maps shared with cargo clients.' },
};

const WPLANS: { id: CreateOrgPayload['plan']; name: string; price: number; limits: string }[] = [
  { id: 'premium', name: 'Premium', price: 450, limits: '500 students · 25 teachers · 1,000 parents' },
  { id: 'enterprise', name: 'Enterprise', price: 650, limits: 'Unlimited students, teachers & parents' },
];

const STEPS = ['Type', 'Details', 'Subscription', 'Review'];

const CreateOrgWizard: React.FC<CreateOrgWizardProps> = ({ onClose, onCreate, initialKind = 'FULL_FEATURE', initialLorryMode = 'SELF_TRACKING' }) => {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState('');

  const [kind, setKind] = useState<OrgKind>(initialKind);
  const [lorryMode, setLorryMode] = useState<LorryModeKind>(initialLorryMode);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const [plan, setPlan] = useState<CreateOrgPayload['plan']>('premium');
  const defaultExpiry = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 12);
    return d.toISOString().split('T')[0];
  }, []);
  const [expiry, setExpiry] = useState(defaultExpiry);
  const [active, setActive] = useState(true);

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const activeKind = ORG_KINDS.find((k) => k.id === kind)!;
  const suffix = kind === 'LORRY' ? LORRY_MODES[lorryMode].suffix : activeKind.suffix;
  const roles = kind === 'LORRY' ? LORRY_MODES[lorryMode].roles : activeKind.roles;
  const cleanCode = (code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const fullCode = (cleanCode || 'CODE') + suffix;

  const canNext = step === 2 ? Boolean(name.trim() && cleanCode && email.trim()) : true;

  function go(n: number) {
    setStep(Math.max(1, Math.min(4, n)));
  }

  async function create() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const result = await onCreate({ kind, lorryMode, name, code, address, phone, email, plan, expiry, active });
      if (result.ok) {
        setCreatedCode(result.fullCode || fullCode);
        setDone(true);
      } else {
        setError(result.error || 'Could not create the organization. Check the details and try again.');
      }
    } catch (e: any) {
      setError(e?.message || 'Something went wrong while creating the organization.');
    } finally {
      setBusy(false);
    }
  }

  function onOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget && !busy) onClose();
  }

  /* ---------------- steps ---------------- */
  function renderStep() {
    if (step === 1)
      return (
        <>
          <h1>What are you creating?</h1>
          <p className="cow-bsub">Pick the kind of account. This sets the roles, the code suffix and the feature set.</p>
          <div className="cow-kchoice">
            {ORG_KINDS.map((k) => (
              <button key={k.id} type="button" className={`cow-kc${kind === k.id ? ' on' : ''}`} onClick={() => setKind(k.id)}>
                <span className="cow-kc-ic" style={{ background: k.grad }}>{k.icon}</span>
                <span>
                  <span className="cow-kc-tt">{k.title}</span>
                  <span className="cow-kc-ds">{k.desc}</span>
                  {k.id !== 'LORRY' && (
                    <span className="cow-kc-role">Roles: {k.roles} · suffix <code>{k.suffix || '—'}</code></span>
                  )}
                </span>
                <span className="cow-kc-check">{kind === k.id && <FaCheck />}</span>
              </button>
            ))}
          </div>
          {kind === 'LORRY' && (
            <div className="cow-ksub">
              <div className="cow-ksl">Lorry type</div>
              <div className="cow-wsubrow">
                {(Object.keys(LORRY_MODES) as LorryModeKind[]).map((m) => (
                  <button key={m} type="button" className={`cow-wsub${lorryMode === m ? ' on' : ''}`} onClick={() => setLorryMode(m)}>
                    {LORRY_MODES[m].label}
                  </button>
                ))}
              </div>
              <div className="cow-kc-role" style={{ marginTop: 12 }}>
                {LORRY_MODES[lorryMode].note} Roles: {LORRY_MODES[lorryMode].roles} · suffix <code>{LORRY_MODES[lorryMode].suffix}</code>
              </div>
            </div>
          )}
        </>
      );

    if (step === 2)
      return (
        <>
          <h1>Organization details</h1>
          <p className="cow-bsub">The basics we'll provision the account with.</p>
          <div className="cow-fld-grid">
            <div className="cow-fld full">
              <label>Organization name <span className="req">*</span></label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Green Valley Academy" />
            </div>
            <div className="cow-fld full">
              <label>Organization code <span className="req">*</span></label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={kind === 'BUS_TRACKING' ? 'e.g. FLEET1' : kind === 'LORRY' ? 'e.g. HAULAGE1' : 'e.g. BROOKSIDE'}
              />
              <span className="cow-codeprev">Final code: <code>{fullCode}</code></span>
            </div>
            <div className="cow-fld">
              <label>Email <span className="req">*</span></label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@school.ac.ke" />
            </div>
            <div className="cow-fld">
              <label>Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+254 7XX XXX XXX" />
            </div>
            <div className="cow-fld full">
              <label>Address</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city" />
            </div>
          </div>
        </>
      );

    if (step === 3)
      return (
        <>
          <h1>Subscription</h1>
          <p className="cow-bsub">Set the plan, when it expires, and whether it's active right away.</p>
          <div className="cow-plan-grid">
            {WPLANS.map((p) => (
              <button key={p.id} type="button" className={`cow-plan${plan === p.id ? ' on' : ''}`} onClick={() => setPlan(p.id)}>
                <div className="cow-wpn">{p.name}</div>
                <div className="cow-wpp">Ksh {p.price}<span>/unit/mo</span></div>
                <div className="cow-wpl">{p.limits}</div>
              </button>
            ))}
          </div>
          <div className="cow-fld-grid" style={{ marginTop: 18 }}>
            <div className="cow-fld">
              <label>Subscription end date <span className="req">*</span></label>
              <input type="date" value={expiry} min={today} onChange={(e) => setExpiry(e.target.value)} />
            </div>
            <div className="cow-fld" style={{ justifyContent: 'flex-end' }}>
              <div className={`cow-toggle${active ? ' on' : ''}`} onClick={() => setActive((a) => !a)}>
                <span className="cow-tsw" />
                <span>
                  <span className="cow-tt">{active ? 'Active' : 'Inactive'}</span>
                  <br />
                  <span className="cow-td">{active ? 'Users can sign in immediately' : 'Created but suspended'}</span>
                </span>
              </div>
            </div>
          </div>
        </>
      );

    // step 4 — review
    const kindLabel = kind === 'LORRY' ? `Lorry · ${LORRY_MODES[lorryMode].label}` : activeKind.title;
    const planDef = WPLANS.find((p) => p.id === plan)!;
    return (
      <>
        <h1>Review &amp; create</h1>
        <p className="cow-bsub">Confirm the details below. You can edit anything from the steps on the left.</p>
        <div className="cow-review">
          <div className="cow-rev-row"><span className="cow-rk">Type</span><span className="cow-rv">{kindLabel}</span></div>
          {roles && <div className="cow-rev-row"><span className="cow-rk">Roles</span><span className="cow-rv">{roles}</span></div>}
          <div className="cow-rev-row"><span className="cow-rk">Organization</span><span className="cow-rv">{name || <em style={{ color: 'var(--faint)' }}>not set</em>}</span></div>
          <div className="cow-rev-row"><span className="cow-rk">Code</span><span className="cow-rv"><code>{fullCode}</code></span></div>
          <div className="cow-rev-row"><span className="cow-rk">Email</span><span className="cow-rv">{email || <em style={{ color: 'var(--faint)' }}>not set</em>}</span></div>
          {phone && <div className="cow-rev-row"><span className="cow-rk">Phone</span><span className="cow-rv">{phone}</span></div>}
          {address && <div className="cow-rev-row"><span className="cow-rk">Address</span><span className="cow-rv">{address}</span></div>}
          <div className="cow-rev-row"><span className="cow-rk">Plan</span><span className="cow-rv">{planDef.name} · {planDef.limits}</span></div>
          <div className="cow-rev-row"><span className="cow-rk">Renews / ends</span><span className="cow-rv">{new Date(expiry).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
          <div className="cow-rev-row">
            <span className="cow-rk">Status</span>
            <span className="cow-rv">
              {active ? (
                <span className="cow-status active"><span className="d" />Active</span>
              ) : (
                <span className="cow-status suspended"><span className="d" />Suspended</span>
              )}
            </span>
          </div>
        </div>
        {error && <div className="cow-err">{error}</div>}
      </>
    );
  }

  return (
    <div className="cow-overlay" onClick={onOverlayClick}>
      <div className="cow">
        <aside className="cow-rail">
          <div className="cow-logo"><span className="cow-logo-mark">P</span>PEAR</div>
          <h2>{done ? 'Organization created' : 'New organization'}</h2>
          <div className="cow-sub">{done ? "It's ready in your console." : 'Provision a school or fleet on PEAR in four steps.'}</div>
          {!done && (
            <div className="cow-steps">
              {STEPS.map((label, i) => {
                const n = i + 1;
                return (
                  <button key={label} type="button" className={`cow-step${step === n ? ' on' : ''}${step > n ? ' done' : ''}`} onClick={() => go(n)}>
                    <span className="cow-sn">{step > n ? <FaCheck /> : n}</span>
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="cow-foot">
            {done ? '' : 'Codes get a suffix automatically (.bus / .fleet / .cargo) so roles route correctly.'}
          </div>
        </aside>

        <div className="cow-body">
          {done ? (
            <div className="cow-scroll">
              <div className="cow-done">
                <div className="cow-done-mark"><FaCheck /></div>
                <h1>{name || 'Organization'} is live</h1>
                <p>The account has been provisioned. Share the code and admin email so they can sign in.</p>
                <div className="cow-done-code"><FaBuilding /> {createdCode}</div>
                <div className="cow-done-actions">
                  <button type="button" className="cow-btn cow-btn-primary" onClick={onClose}>Back to console</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="cow-scroll">{renderStep()}</div>
              <div className="cow-footbar">
                <button type="button" className="cow-btn cow-btn-line" onClick={() => (step === 1 ? onClose() : go(step - 1))} disabled={busy}>
                  <FaArrowLeft /> {step === 1 ? 'Cancel' : 'Back'}
                </button>
                {step < 4 ? (
                  <button type="button" className="cow-btn cow-btn-primary" disabled={!canNext} onClick={() => canNext && go(step + 1)}>
                    Continue <FaArrowRight />
                  </button>
                ) : (
                  <button type="button" className="cow-btn cow-btn-primary" onClick={create} disabled={busy}>
                    <FaPlus /> {busy ? 'Creating…' : 'Create organization'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateOrgWizard;
